import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../assets/constant';
import "./subscriber-preferences.css";

export const SubscriberPreferences = () => {
    const [preferences, setPreferences] = useState({
        whatsapp_enabled: true,
        sms_enabled: true,
        email_enabled: true,
        ivr_enabled: true
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [unsubscribing, setUnsubscribing] = useState(null);
    const [unsubscribeSuccess, setUnsubscribeSuccess] = useState(null);
    const [unsubscribeError, setUnsubscribeError] = useState(null);
    const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
    const [selectedUnsubscribeChannel, setSelectedUnsubscribeChannel] = useState(null);

    // Get user data from Redux
    const user = useSelector((state) => state.auth?.user);
    const token = useSelector((state) => state.auth?.token);
    const subscriberId = user?.id || user?.userId;

    // Channel configuration
    const channels = [
        {
            id: 'whatsapp',
            label: 'WhatsApp',
            icon: 'fa-brands fa-whatsapp',
            color: '#25D366',
            description: 'Get notifications via WhatsApp',
            enabledKey: 'whatsapp_enabled'
        },
        {
            id: 'sms',
            label: 'SMS',
            icon: 'fa-regular fa-message',
            color: '#FF6B6B',
            description: 'Get notifications via SMS',
            enabledKey: 'sms_enabled'
        },
        {
            id: 'email',
            label: 'Email',
            icon: 'fa-regular fa-envelope',
            color: '#4ECDC4',
            description: 'Get notifications via Email',
            enabledKey: 'email_enabled'
        },
        {
            id: 'ivr',
            label: 'IVR Call',
            icon: 'fa-solid fa-headset',
            color: '#6C63FF',
            description: 'Get call reminders',
            enabledKey: 'ivr_enabled'
        }
    ];

    const channelNames = {
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        email: 'Email',
        ivr: 'IVR Call'
    };

    const fetchPreferences = async () => {
        if (!subscriberId || !token) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/v1/subscribers/${subscriberId}/preferences`;

            const response = await axios.get(
                url,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data?.success) {
                const data = response.data.data;

                setPreferences({
                    whatsapp_enabled: data.whatsapp_enabled === true || data.whatsapp_enabled === 1,
                    sms_enabled: data.sms_enabled === true || data.sms_enabled === 1,
                    email_enabled: data.email_enabled === true || data.email_enabled === 1,
                    ivr_enabled: data.ivr_enabled === true || data.ivr_enabled === 1
                });
            }
        } catch (error) {
            console.error('Error fetching preferences:', error);
            if (error.response?.status === 404) {
            } else if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
            } else {
                setError('Failed to load preferences. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const savePreferences = async () => {
        if (!subscriberId || !token) {
            setError('Please login to save preferences');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(false);

        try {
            // const url = `https://dev.recallalarms.com/api/v1/subscribers/${subscriberId}/preferences`;
            const url = `${API_BASE_URL}/v1/subscribers/${subscriberId}/preferences`;

            const payload = {
                whatsapp_enabled: preferences.whatsapp_enabled === true || preferences.whatsapp_enabled === 1,
                sms_enabled: preferences.sms_enabled === true || preferences.sms_enabled === 1,
                email_enabled: preferences.email_enabled === true || preferences.email_enabled === 1,
                ivr_enabled: preferences.ivr_enabled === true || preferences.ivr_enabled === 1
            };

            const response = await axios.put(
                url,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data?.success) {
                setSuccess(true);
                setTimeout(() => {
                    setSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
            } else if (error.response?.status === 400) {
                setError(error.response.data?.message || 'Validation failed. Please check your input.');
            } else {
                setError('Failed to save preferences. Please try again.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = (channelKey) => {
        setPreferences(prev => ({
            ...prev,
            [channelKey]: !prev[channelKey]
        }));
    };

    // ========== UNSUBSCRIBE - Show Confirm ==========
    const handleUnsubscribeClick = (channel) => {
        setSelectedUnsubscribeChannel(channel);
        setShowUnsubscribeConfirm(true);
    };

    // ========== UNSUBSCRIBE - Confirm ==========
    const confirmUnsubscribe = async () => {
        const channel = selectedUnsubscribeChannel;
        if (!channel) return;

        setShowUnsubscribeConfirm(false);
        setUnsubscribing(channel);
        setUnsubscribeError(null);
        setUnsubscribeSuccess(null);

        try {
            const unsubscribeUrl = `https://dev.recallalarms.com/api/v1/unsubscribe`;

            await axios.post(
                unsubscribeUrl,
                {
                    subscriber_id: subscriberId,
                    channel: channel,
                    reason: 'User requested from settings page',
                    source: 'settings_page'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const prefUrl = `https://dev.recallalarms.com/api/v1/subscribers/${subscriberId}/preferences`;
            const enabledKey = `${channel}_enabled`;

            const updatedPreferences = {
                ...preferences,
                [enabledKey]: false
            };

            const payload = {
                whatsapp_enabled: updatedPreferences.whatsapp_enabled === true || updatedPreferences.whatsapp_enabled === 1,
                sms_enabled: updatedPreferences.sms_enabled === true || updatedPreferences.sms_enabled === 1,
                email_enabled: updatedPreferences.email_enabled === true || updatedPreferences.email_enabled === 1,
                ivr_enabled: updatedPreferences.ivr_enabled === true || updatedPreferences.ivr_enabled === 1
            };

            await axios.put(
                prefUrl,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Step 3: Update local state
            setPreferences(updatedPreferences);
            setUnsubscribeSuccess(channel);

            setTimeout(() => setUnsubscribeSuccess(null), 3000);

        } catch (error) {
            console.error('Error unsubscribing:', error);
            setUnsubscribeError(channel);
            setTimeout(() => setUnsubscribeError(null), 3000);
        } finally {
            setUnsubscribing(null);
            setSelectedUnsubscribeChannel(null);
        }
    };

    // ========== UNSUBSCRIBE - Cancel ==========
    const cancelUnsubscribe = () => {
        setShowUnsubscribeConfirm(false);
        setSelectedUnsubscribeChannel(null);
    };

    useEffect(() => {
        fetchPreferences();
    }, []);

    const handleRedirect = () => {
        window.history.back();
    };

    // ========== RENDER ==========
    return (
        <div className="subscriber-container">
            <div className="subscriber-preferences">
                <div className="preferences-header">
                    <div className="header-left">
                        <button
                            className="notification-back-btn"
                            onClick={handleRedirect}
                            aria-label="Go back"
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <div className="header-title">
                            <h3>
                                Notification Preferences
                            </h3>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading your preferences...</p>
                    </div>
                ) : (
                    <>
                        {/* Error/Success Messages */}
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                <i className="fas fa-exclamation-circle me-2"></i>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
                                <i className="fas fa-check-circle me-2"></i>
                                Preferences saved successfully!
                            </Alert>
                        )}

                        {/* Unsubscribe Success/Error Messages */}
                        {unsubscribeSuccess && (
                            <Alert variant="success" onClose={() => setUnsubscribeSuccess(null)} dismissible>
                                <i className="fas fa-check-circle me-2"></i>
                                Successfully unsubscribed from {channelNames[unsubscribeSuccess]} notifications
                            </Alert>
                        )}

                        {unsubscribeError && (
                            <Alert variant="danger" onClose={() => setUnsubscribeError(null)} dismissible>
                                <i className="fas fa-exclamation-circle me-2"></i>
                                Failed to unsubscribe from {channelNames[unsubscribeError]}. Please try again.
                            </Alert>
                        )}

                        <div className="preferences-list">
                            {channels.map((channel) => {
                                const isEnabled = preferences[channel.enabledKey];
                                const isUnsubscribing = unsubscribing === channel.id;

                                return (
                                    <div key={channel.id} className="preference-item">
                                        <div className="preference-info">
                                            <div className="preference-icon" style={{ color: channel.color }}>
                                                <i className={channel.icon}></i>
                                            </div>
                                            <div>
                                                <h6>{channel.label}</h6>
                                                <span className="text-muted">{channel.description}</span>
                                            </div>
                                        </div>
                                        <div className="preference-controls">
                                            {/* <button
                                                className="unsubscribe-link"
                                                onClick={() => handleUnsubscribeClick(channel.id)}
                                                disabled={isUnsubscribing || !isEnabled}
                                            >
                                                {isUnsubscribing ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    'Unsubscribe'
                                                )}
                                            </button> */}
                                            <Form.Check
                                                type="switch"
                                                id={`${channel.id}-toggle`}
                                                checked={isEnabled}
                                                onChange={() => handleToggle(channel.enabledKey)}
                                                className="preference-toggle"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="preferences-footer">
                            <small className="text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                Toggle ON/OFF to temporarily enable/disable.
                                Unsubscribe to permanently stop receiving notifications.
                            </small>
                        </div>

                        <div className="preferences-actions">
                            <Button
                                className="save-preferance-btn"
                                onClick={savePreferences}
                                disabled={isSaving || isLoading}
                            >
                                {isSaving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Preferences'
                                )}
                            </Button>
                        </div>
                    </>
                )}

                {/* Unsubscribe Confirmation - Inline */}
                {showUnsubscribeConfirm && (
                    <div className="unsubscribe-confirm-overlay">
                        <div className="unsubscribe-confirm-box">
                            <div className="unsubscribe-confirm-header">
                                <div className='heading-wrapper'>
                                    <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                                    <h6 className="mb-0">Confirm Unsubscribe</h6>
                                </div>
                                <button className="close-btn" onClick={cancelUnsubscribe}>×</button>
                            </div>
                            <div className="unsubscribe-confirm-body">
                                <p className="mb-2">
                                    Unsubscribe from
                                    <strong> {selectedUnsubscribeChannel ? channelNames[selectedUnsubscribeChannel] : ''}</strong>?
                                </p>
                                <p className="text-muted small mb-0">
                                    You can re-enable anytime from settings.
                                </p>
                            </div>
                            <div className="unsubscribe-confirm-footer">
                                <Button variant="secondary" size="sm" onClick={cancelUnsubscribe}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={confirmUnsubscribe}
                                >
                                    Yes, Unsubscribe
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};